import React, { FC, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Cart3, Dash, Plus, CheckCircleFill } from 'react-bootstrap-icons';

import { useSelector } from 'store';
import { IPrice } from 'api/types';
import { AddToCartButton } from 'components/containers';
import WiseModal from 'components/wise/WiseModal';

import styles from 'theme/pages/wise/WiseProduct/components/WiseMobileBuyBar/WiseMobileBuyBar.module.scss';

interface IProps {
  productId: number;
  prices: IPrice[];
  productName?: string;
  productImage?: string;
}

const formatPrice = (raw: number | null | undefined) => {
  if (raw == null || Number.isNaN(raw)) return '-';
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(raw);
};

const WiseMobileBuyBar: FC<IProps> = ({ productId, prices, productName, productImage }) => {
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.auth);

  const hasPrices = prices.length > 0;
  const firstPrice = prices[0];

  const [isOpen, setIsOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(firstPrice?.unit_id ?? null);
  const [quantity, setQuantity] = useState<number>(firstPrice?.minimal_quantity ?? 1);

  useEffect(() => {
    if (!selectedUnitId && firstPrice) {
      setSelectedUnitId(firstPrice.unit_id);
    }
  }, [firstPrice, selectedUnitId]);

  const activePrice = useMemo(
    () => prices.find((p) => p.unit_id === selectedUnitId) ?? firstPrice,
    [prices, selectedUnitId, firstPrice]
  );

  const minimalQuantity = activePrice?.minimal_quantity ?? 1;

  const totalNet = useMemo(() => {
    if (!activePrice) return null;
    return (activePrice.price_net ?? 0) * quantity;
  }, [activePrice, quantity]);

  const totalGross = useMemo(() => {
    if (!activePrice) return null;
    return (activePrice.price_gross ?? 0) * quantity;
  }, [activePrice, quantity]);

  const handleDec = () => setQuantity((q) => Math.max(minimalQuantity, q - 1));
  const handleInc = () => setQuantity((q) => q + 1);
  const handleQtyChange = (val: string) => {
    const n = parseInt(val, 10);
    if (Number.isNaN(n)) {
      setQuantity(minimalQuantity);
      return;
    }
    setQuantity(Math.max(minimalQuantity, n));
  };

  const handleSelectUnit = (newUnitId: number) => {
    setSelectedUnitId(newUnitId);
    const newPrice = prices.find((p) => p.unit_id === newUnitId);
    if (newPrice) setQuantity(newPrice.minimal_quantity ?? 1);
  };

  if (!hasPrices || !activePrice) return null;

  const isGuest = profile?.role === 'ROLE_OPEN_PROFILE';

  return (
    <>
      <div
        className={classnames(
          styles.bar,
          'StylePath-Pages-Wise-WiseProduct-MobileBuyBar'
        )}>
        <div className={styles.barPrice}>
          <div className={styles.barPriceLabel}>
            <Trans>Cena netto</Trans>
          </div>
          <div className={styles.barPriceAmount}>
            {formatPrice(activePrice.price_net)}{' '}
            <span className={styles.barPriceCurrency}>{activePrice.currency}</span>
          </div>
          <div className={styles.barPriceHint}>
            {formatPrice(activePrice.price_gross)} {activePrice.currency} <Trans>brutto</Trans>
          </div>
        </div>

        <button
          type="button"
          className={styles.barButton}
          onClick={() => setIsOpen(true)}
          disabled={isGuest}>
          <Cart3 />
          <span>
            <Trans>Dodaj do koszyka</Trans>
          </span>
        </button>
      </div>

      <WiseModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('Dodaj do koszyka')}
        maxWidth={520}>
        <div className={styles.modalBody}>
          {productName && (
            <div className={styles.productSummary}>
              {productImage && (
                <div className={styles.productImage}>
                  <img src={productImage} alt={productName} />
                </div>
              )}
              <div className={styles.productInfo}>
                <div className={styles.productName}>{productName}</div>
              </div>
            </div>
          )}

          {prices.length > 1 && (
            <div className={styles.unitSection}>
              <div className={styles.sectionLabel}>
                <Trans>Wybierz jednostkę</Trans>
              </div>
              <div className={styles.unitList}>
                {prices.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={classnames(styles.unitItem, {
                      [styles.unitItemActive]: p.unit_id === selectedUnitId
                    })}
                    onClick={() => handleSelectUnit(p.unit_id)}>
                    <div className={styles.unitItemLeft}>
                      <span className={styles.unitRadio}>
                        {p.unit_id === selectedUnitId && <CheckCircleFill />}
                      </span>
                      <div className={styles.unitItemText}>
                        <div className={styles.unitName}>{p.unit_name}</div>
                        <div className={styles.unitConverter}>
                          <Trans>Ilość</Trans>: {p.converter}
                        </div>
                      </div>
                    </div>
                    <div className={styles.unitPrice}>
                      <strong>
                        {p.price_net_formatted} {p.currency}
                      </strong>
                      <span>
                        {p.price_gross_formatted} {p.currency} <Trans>brutto</Trans>
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.quantitySection}>
            <div className={styles.sectionLabel}>
              <Trans>Ilość</Trans>
            </div>
            <div className={styles.counter}>
              <button
                type="button"
                className={styles.counterBtn}
                onClick={handleDec}
                disabled={quantity <= minimalQuantity}
                aria-label={t('Zmniejsz')}>
                <Dash />
              </button>
              <input
                type="number"
                className={styles.counterInput}
                value={quantity}
                onChange={(e) => handleQtyChange(e.target.value)}
                min={minimalQuantity}
                aria-label={t('Ilość')}
              />
              <button
                type="button"
                className={styles.counterBtn}
                onClick={handleInc}
                aria-label={t('Zwiększ')}>
                <Plus />
              </button>
            </div>
            {minimalQuantity > 1 && (
              <div className={styles.minHint}>
                <Trans>Minimalna ilość</Trans>: <strong>{minimalQuantity}</strong>
              </div>
            )}
          </div>

          <div className={styles.totalsBox}>
            <div className={styles.totalsRow}>
              <span className={styles.totalsLabel}>
                <Trans>Wartość netto</Trans>
              </span>
              <span className={styles.totalsValue}>
                {formatPrice(totalNet)} {activePrice.currency}
              </span>
            </div>
            <div className={classnames(styles.totalsRow, styles.totalsRowHint)}>
              <span>
                <Trans>Wartość brutto</Trans>
              </span>
              <span>
                {formatPrice(totalGross)} {activePrice.currency}
              </span>
            </div>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.btnGhost}
              onClick={() => setIsOpen(false)}>
              <Trans>Anuluj</Trans>
            </button>
            <div className={styles.addToCartWrap}>
              <AddToCartButton
                quantity={quantity}
                unitId={selectedUnitId ?? activePrice.unit_id}
                productId={productId}
                disabled={isGuest}
                large
              />
            </div>
          </div>
        </div>
      </WiseModal>
    </>
  );
};

export default WiseMobileBuyBar;
